/* Dependencies */
import React, { useState, useMemo } from 'react';

/* Translations */
import { useTranslation } from 'react-i18next';

/* Components */
import { Form, Formik } from 'formik';
import RenderIf from 'components/RenderIf';
import { Selecter } from 'lego-dist';
import { useRouter } from 'routes';

/* Styled Components */
import {
  PermissionsWrapper,
  Content,
  Label,
  InputGroup,
  SelectsWrapper,
  TitleText,
  DescriptionText,
} from '../styles';

/* Services, Hooks, Constants */
import * as Yup from 'yup';
import useCenters from 'hooks/centers/UseCenters';

export interface AssingCentersAndClassroomsProps {
  nextStep?: any;
  renderFooter?: any;
  formData?: any;
}

const AssingCentersAndClassrooms: React.FC<AssingCentersAndClassroomsProps> = ({
  renderFooter,
  formData,
}) => {
  const { t } = useTranslation();
  const {
    currentParamsRef: {
      current: { centerId, groupId },
    },
  } = useRouter();
  const [selectedCenters, setSelectedCenters] = useState<any[]>([]);
  const { centerList = [] } = useCenters();
  const initialValues = {
    centers: [Number(centerId)],
    classrooms: formData?.classrooms || [Number(groupId)],
  };

  /* Validation */
  const classroomsForm = Yup.object().shape({
    classrooms: Yup.array().required(t('validations:FIELD_REQUIRED')),
    centers: Yup.array().required(t('validations:FIELD_REQUIRED')),
  });

  /**
   * Computed prop: Prepare centersList to be used in Selecter
   */
  const centersList = useMemo(
    () => centerList.map((center: any) => {
      const hasCenters = initialValues?.centers?.length;
      const isSameCenter = Number(centerId) === center?.id;

      if (hasCenters && isSameCenter) {
        setSelectedCenters([
          {
            ...center,
            name: `${center.name}`,
            label: `${center.name}`,
            value: center.id,
          },
        ]);
      }

      return {
        ...center,
        name: `${center.name}`,
        label: `${center.name}`,
        value: center.id,
      };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [centerId, centerList, initialValues?.centers?.length],
  );

  const classroomsList = useMemo(
    () => {
      const values = selectedCenters
        .flatMap((center: any) => {
          return center.groups.map((group: any) => {
            group.parent_id = center.id;
            group.parent = {
              id: center.id,
              name: center.name,
            };

            return group;
          });
        })
        .map((group: any) => ({
          ...group,
          name: group.name,
          label: group.name,
          value: group.id,
          parent_id: group.parent_id,
          parent: group.parent,
        }));

      return values;
    },
    [selectedCenters],
  );

  return (
    <PermissionsWrapper>
      <Formik
        initialValues={initialValues}
        validationSchema={classroomsForm}
        onSubmit={() => {}}
        validateOnBlur
        validateOnChange
        isInitialValid={initialValues?.centers?.length && initialValues?.classrooms?.length}
        render={({ setFieldValue, errors, values, isValid }) => (
          <Form>
            <Content>
              <TitleText>{t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.CLASSROOMS.TITLE')}</TitleText>
              <DescriptionText>{t('settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.CLASSROOMS.DESCRIPTION')}</DescriptionText>
              <SelectsWrapper>
                <InputGroup>
                  <Label>
                    <span>{t('settings:CENTERS')}</span>
                  </Label>
                  <Selecter
                    data={centersList}
                    name="centers"
                    onChange={(value: any) => {
                      setFieldValue('centers', value);
                      setSelectedCenters(value);
                    }}
                    value={values?.centers}
                    placeholder={t('calendar_events:SELECT_OPTION')}
                    multiple
                    checkbox
                    search
                  />
                </InputGroup>
                <InputGroup>
                  <Label>
                    <span>{t('settings:CLASSROOMS')}</span>
                  </Label>
                  <Selecter
                    data={classroomsList}
                    groupBy="parent"
                    onChange={(value: any) => setFieldValue('classrooms', value)}
                    value={values.classrooms}
                    placeholder={t('calendar_events:SELECT_OPTION')}
                    multiple
                    checkbox
                    search
                  />
                </InputGroup>
              </SelectsWrapper>
            </Content>
            <RenderIf condition={renderFooter}>
              {renderFooter(isValid, {
                centersWithClassrooms: values,
              })}
            </RenderIf>
          </Form>
        )}
      />
    </PermissionsWrapper>
  );
};

export default AssingCentersAndClassrooms;
